import { getProductInvoiceLinkStatus } from '@/components/productInvoiceLinkStatus'
import {
  isReconcileIgnored,
  qboRowReconcileMonth,
} from '@/components/qboMatchStatus'
import { dateToReportMonth } from '@/components/uploadsMonthOptions'
import type {
  WarehouseBillingAwarenessItem,
  WarehouseBillingLineItem,
  WarehouseInvoiceSummary,
  WarehouseQboBillMatch,
} from '@/services/warehouseTypes'
import { uploadsErrorVisibleForMonth } from '@/utils/completedCalendarMonth'

export type UploadsContainerErrorCounts = {
  wh: number
  product: number
  billing: number
  qbo: number
}

function isWhServiceInvoice(invoice: WarehouseInvoiceSummary): boolean {
  return (
    invoice.invoice_type === 'wh_service' ||
    (invoice.invoice_type === 'mixed' && Boolean(invoice.report_month))
  )
}

function isProductInvoice(invoice: WarehouseInvoiceSummary): boolean {
  if (invoice.invoice_type === 'wh_service') return false
  return !(invoice.report_month && invoice.invoice_type === 'mixed')
}

export function whInvoiceErrorMonth(invoice: WarehouseInvoiceSummary): string | null {
  return invoice.report_month ?? dateToReportMonth(invoice.invoice_date)
}

export function productInvoiceErrorMonth(invoice: WarehouseInvoiceSummary): string | null {
  return dateToReportMonth(invoice.invoice_date)
}

/** WH invoice row has missing storage or admin fee (extraction / package gap). */
export function whInvoiceHasFeeError(invoice: WarehouseInvoiceSummary): boolean {
  return invoice.storage_fee_total == null || invoice.admin_fee_total == null
}

/**
 * All-months WH container errors: fee-null WH invoices plus billing months that
 * still need a non-legacy WH invoice.
 */
export function countWhUploadsErrors(
  invoices: WarehouseInvoiceSummary[],
  billingLines: WarehouseBillingLineItem[],
  asOf: Date = new Date()
): number {
  const whInvoices = invoices.filter(isWhServiceInvoice)
  const feeErrorCount = whInvoices.filter(
    (invoice) =>
      whInvoiceHasFeeError(invoice) &&
      uploadsErrorVisibleForMonth(whInvoiceErrorMonth(invoice), asOf)
  ).length

  const billingByMonth = new Map<string, { isLegacy: boolean }>()
  for (const line of billingLines) {
    const existing = billingByMonth.get(line.report_month)
    if (!existing) {
      billingByMonth.set(line.report_month, { isLegacy: line.is_legacy })
    } else if (!line.is_legacy) {
      existing.isLegacy = false
    }
  }

  let missingWhCount = 0
  for (const [month, { isLegacy }] of billingByMonth) {
    if (isLegacy) continue
    if (!uploadsErrorVisibleForMonth(month, asOf)) continue
    const hasWh = whInvoices.some((invoice) => invoice.report_month === month)
    if (!hasWh) missingWhCount += 1
  }

  return feeErrorCount + missingWhCount
}

/** Product invoice line totals do not match extracted invoice amounts. */
export function productInvoiceHasAllocationError(invoice: WarehouseInvoiceSummary): boolean {
  return invoice.allocation_valid === false
}

export function countBillingQtyMismatches(
  awarenessRows: WarehouseBillingAwarenessItem[],
  asOf: Date = new Date()
): number {
  return awarenessRows.filter(
    (row) =>
      row.qty_mismatch && uploadsErrorVisibleForMonth(row.report_month, asOf)
  ).length
}

/** Billing lines missing a linked ledger txn plus qty mismatches on linked lines. */
export function countBillingUploadsErrors(
  billingLines: WarehouseBillingLineItem[],
  qtyMismatchCount = 0,
  asOf: Date = new Date()
): number {
  const unlinkedCount = billingLines.filter(
    (line) =>
      line.linked_bk_txn_id == null &&
      uploadsErrorVisibleForMonth(line.report_month, asOf)
  ).length
  return unlinkedCount + qtyMismatchCount
}

/** Product invoices that are not fully linked/ignored or have allocation mismatch. */
export function countProductUploadsErrors(
  invoices: WarehouseInvoiceSummary[],
  asOf: Date = new Date()
): number {
  return invoices.filter((invoice) => {
    if (!isProductInvoice(invoice)) return false
    if (!uploadsErrorVisibleForMonth(productInvoiceErrorMonth(invoice), asOf)) return false
    if (productInvoiceHasAllocationError(invoice)) return true
    const status = getProductInvoiceLinkStatus(invoice)
    return status === 'not_linked' || status === 'partial'
  }).length
}

/**
 * QBO rows needing review across all months (excludes matched/clean, direct-no-holding,
 * and ignored rows).
 */
export function countQboUploadsErrors(
  matches: WarehouseQboBillMatch[],
  fallbackMonth: string | null | undefined = null,
  asOf: Date = new Date()
): number {
  return matches.filter((match) => {
    if (
      match.status === 'matched' ||
      match.status === 'clean' ||
      match.status === 'direct_no_holding'
    ) {
      return false
    }
    const rowMonth = qboRowReconcileMonth(match, fallbackMonth)
    if (rowMonth != null && !uploadsErrorVisibleForMonth(rowMonth, asOf)) {
      return false
    }
    return !isReconcileIgnored(match.reconcile_ignores, rowMonth)
  }).length
}

export function sumUploadsErrorCounts(counts: UploadsContainerErrorCounts): number {
  return counts.wh + counts.product + counts.billing + counts.qbo
}

export function uploadsErrorBadgeText(count: number): string {
  if (count > 99) return '99+'
  return String(count)
}
